import { useState, useRef, useEffect } from 'react'
import { Mic, MicOff, MessageSquare, X, Send, Loader2, SquarePen, Copy, Check, HelpCircle } from 'lucide-react'
import { Message } from '@renderer/types/api'
import { ThinkingState, getAgentAudioAnalyser, resumeAgentAudioContext } from '@renderer/hooks/useLiveKitAgent'
import { ThinkingAccordion } from './ThinkingAccordion'
import { CodeBlock } from './CodeBlock'

interface ChatOverlayProps {
  messages: Message[]
  isTyping: boolean
  isSending: boolean
  inputValue: string
  onInputChange: (v: string) => void
  onSend: () => void
  onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void
  lkConnected: boolean
  lkConnecting: boolean
  lkTalking: boolean
  lkAgentStatus: 'idle' | 'listening' | 'processing' | 'speaking'
  lkSendScreenshot: (dataUrl: string) => void
  lkStartTalking: () => Promise<void>
  lkStopTalking: () => Promise<void>
  onNewChat: () => void
  thinking?: ThinkingState | null
  assistMode: 'guide' | 'assist'
  onHelp: (() => void) | null
  helpPending: boolean
  helpRejection: string | null
}

export const ChatOverlay: React.FC<ChatOverlayProps> = ({
  messages,
  isTyping,
  isSending,
  inputValue,
  onInputChange,
  onSend,
  onKeyPress,
  lkConnected,
  lkConnecting,
  lkTalking,
  lkAgentStatus,
  lkSendScreenshot,
  lkStartTalking,
  lkStopTalking,
  onNewChat,
  thinking,
  assistMode,
  onHelp,
  helpPending,
  helpRejection,
}) => {
  const [open, setOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)
  const [helpNotice, setHelpNotice] = useState<string | null>(null)
  const lastSeenIdRef = useRef<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const beginTurnRef = useRef<() => Promise<void>>(async () => {})
  const endTurnRef = useRef<() => Promise<void>>(async () => {})

  beginTurnRef.current = async () => { await lkStartTalking() }
  endTurnRef.current = async () => { await lkStopTalking() }

  const toggleMic = async () => {
    if (lkTalking) {
      await endTurnRef.current()
    } else {
      await beginTurnRef.current()
    }
  }

  useEffect(() => {
    if (!lkConnected) return

    const isPushToTalk = (e: KeyboardEvent) =>
      e.code === 'Space' && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey

    let holding = false

    const onKeyDown = (e: KeyboardEvent) => {
      if (!isPushToTalk(e)) return
      e.preventDefault()
      e.stopPropagation()
      if (holding) return
      holding = true
      beginTurnRef.current()
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return
      if (!holding) return
      e.preventDefault()
      e.stopPropagation()
      holding = false
      endTurnRef.current()
    }

    document.addEventListener('keydown', onKeyDown, { capture: true })
    document.addEventListener('keyup', onKeyUp, { capture: true })
    return () => {
      document.removeEventListener('keydown', onKeyDown, { capture: true } as any)
      document.removeEventListener('keyup', onKeyUp, { capture: true } as any)
    }
  }, [lkConnected])

  useEffect(() => {
    if (open) {
      lastSeenIdRef.current = messages[messages.length - 1]?.id ?? null
      setUnreadCount(0)
      return
    }
    if (!messages.length) return
    const lastSeenIdx = lastSeenIdRef.current
      ? messages.findIndex((m) => m.id === lastSeenIdRef.current)
      : -1
    const newCount = messages
      .slice(lastSeenIdx + 1)
      .filter((m) => m.sender === 'agent').length
    setUnreadCount(newCount)
  }, [messages, open])

  useEffect(() => {
    if (!open) return
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, open, isTyping])

  useEffect(() => {
    if (!helpRejection) return
    const why: Record<string, string> = {
      session_not_started: 'The tutor is still getting started.',
      not_assist_mode: 'The tutor is already guiding you.',
      busy: "The tutor is already on it — one moment.",
      tutor_speaking: 'The tutor is talking — listen, then ask again.',
      failed: "That didn't go through — try again."
    }
    setHelpNotice(why[helpRejection] ?? "That didn't go through.")
    const t = setTimeout(() => setHelpNotice(null), 4000)
    return () => clearTimeout(t)
  }, [helpRejection])

  return (
    <>
      {/* Single bottom bar: pill centered, buttons right */}
      <div className="absolute inset-x-3 bottom-3 flex items-center z-10 pointer-events-none">
        {/* Left spacer to balance buttons on the right */}
        <div className="flex-1" />
        {/* Hint pill — centered */}
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] pointer-events-none shrink-0"
          style={{
            background: 'rgba(255,255,255,0.92)',
            border: '1px solid #e4d6b0',
            color: '#4a3f2a',
            backdropFilter: 'blur(8px)',
            transition: 'background 200ms'
          }}
        >
          {helpNotice ? (
            <>
              <HelpCircle className="w-3 h-3 shrink-0" style={{ color: '#a8543a' }} />
              <span className="truncate">{helpNotice}</span>
            </>
          ) : lkTalking ? (
            <>
              <MicVisualizer />
              <span className="truncate">Listening… release Ctrl+Space</span>
            </>
          ) : lkAgentStatus === 'processing' ? (
            <>
              <Loader2 className="w-3 h-3 shrink-0 animate-spin" style={{ color: '#7a5a1e' }} />
              <span className="truncate">Processing…</span>
            </>
          ) : lkAgentStatus === 'speaking' ? (
            <>
              <SpeakingVisualizer />
              <span className="truncate">Speaking…</span>
            </>
          ) : (
            <>
              <kbd
                className="px-1 py-0.5 rounded text-[9px] font-mono shrink-0"
                style={{ background: '#faf4e3', border: '1px solid #e4d6b0', color: '#1d160a' }}
              >
                Ctrl+Space
              </kbd>
              <span className="truncate">Hold to talk</span>
            </>
          )}
        </div>

        {/* Right spacer wrapping buttons — balances left spacer so pill is truly centered */}
        <div className="flex-1 flex justify-end">
          <div className="flex items-center gap-2 shrink-0 pointer-events-auto">
          {assistMode === 'assist' && (
            <div className="relative group">
              <button
                onClick={() => onHelp?.()}
                disabled={!onHelp || helpPending}
                aria-busy={helpPending}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-medium shadow-md transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100 disabled:cursor-not-allowed"
                style={{ background: '#ffffff', border: '1px solid #e4d6b0', color: '#1d160a' }}
              >
                {helpPending ? (
                  <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" style={{ color: '#b8923a' }} />
                ) : (
                  <HelpCircle className="w-3.5 h-3.5 shrink-0" style={{ color: '#b8923a' }} />
                )}
                <span className="hidden sm:inline">
                  {helpPending ? 'Asking…' : "What's next?"}
                </span>
              </button>

              <div
                role="tooltip"
                className="pointer-events-none absolute bottom-full right-0 mb-2 w-60 rounded-lg px-2.5 py-2 text-[11px] leading-snug opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0"
                style={{
                  background: '#2a2113',
                  color: '#f5ecd6',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.25)'
                }}
              >
                {onHelp
                  ? 'The tutor looks at your screen and tells you the one next thing to do, then goes quiet again.'
                  : 'The tutor is still joining — one moment.'}
              </div>
            </div>
          )}

          <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-medium shadow-md transition-transform hover:scale-[1.02]"
            style={{ background: '#ffffff', border: '1px solid #e4d6b0', color: '#1d160a' }}
          >
            <MessageSquare className="w-3.5 h-3.5 shrink-0" style={{ color: '#b8923a' }} />
            <span className="hidden sm:inline">Chat with Agent</span>
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-bold text-white"
                style={{ background: '#7a5a1e' }}
              >
                {unreadCount}
              </span>
            )}
          </button>

          <button
            onClick={toggleMic}
            disabled={!lkConnected}
            title={
              !lkConnected
                ? lkConnecting
                  ? 'Connecting to instructor…'
                  : 'Instructor unavailable'
                : lkTalking
                  ? 'Click to stop'
                  : 'Click to talk (or hold Ctrl+Space)'
            }
            className="grid place-items-center w-10 h-10 rounded-full shadow-md transition-transform hover:scale-[1.05] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: lkTalking
                ? '#a8543a'
                : 'linear-gradient(135deg, #7a5a1e, #b8923a)',
              color: '#ffffff',
              boxShadow: lkTalking
                ? '0 0 0 3px rgba(168, 84, 58, 0.25), 0 4px 12px rgba(0,0,0,0.15)'
                : '0 4px 12px rgba(122, 90, 30, 0.3)'
            }}
          >
            {lkTalking ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
          </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="absolute right-4 flex flex-col rounded-2xl shadow-2xl overflow-hidden z-20"
          style={{
            bottom: 80,
            width: 380,
            height: 'min(60vh, 520px)',
            background: '#ffffff',
            border: '1px solid #e4d6b0'
          }}
        >
          <div
            className="shrink-0 flex items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: '#f0e6cd', background: '#faf4e3' }}
          >
            <div>
              <p
                className="text-[10px] font-semibold tracking-wider uppercase"
                style={{ color: '#b8923a' }}
              >
                Aurilearn Lab Assistant
              </p>
              <p className="text-[13px] font-semibold flex items-center gap-2" style={{ color: '#1d160a' }}>
                Live help
                {lkConnected ? (
                  <span
                    className="inline-flex w-1.5 h-1.5 rounded-full"
                    style={{ background: '#6b8a3a' }}
                    title="connected to instructor"
                  />
                ) : (
                  <span
                    className="inline-flex w-1.5 h-1.5 rounded-full"
                    style={{ background: '#a89c7e' }}
                    title="not connected"
                  />
                )}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={onNewChat}
                title="New session"
                className="grid place-items-center w-7 h-7 rounded-md transition-colors"
                style={{ color: '#7a6e54' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0e6cd'
                  e.currentTarget.style.color = '#1d160a'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = '#7a6e54'
                }}
              >
                <SquarePen className="w-4 h-4" />
              </button>
              <button
                onClick={() => setOpen(false)}
                title="Close"
                className="grid place-items-center w-7 h-7 rounded-md transition-colors"
                style={{ color: '#7a6e54' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f0e6cd'
                  e.currentTarget.style.color = '#1d160a'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = '#7a6e54'
                }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && !isTyping && (
              <div className="text-center text-[13px] py-8" style={{ color: '#7a6e54' }}>
                Ask anything about the current step.
              </div>
            )}
            {messages.map((m) => (
              <ChatBubble key={m.id} msg={m} />
            ))}
            {thinking && (
              <ThinkingAccordion
                thinking={thinking.text}
                isActive={!thinking.done}
              />
            )}
          </div>

          <div className="shrink-0 p-3 border-t" style={{ borderColor: '#f0e6cd' }}>
            <div
              className="flex items-center gap-2 rounded-xl px-3 py-2"
              style={{ background: '#faf4e3', border: '1px solid #e4d6b0' }}
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={onKeyPress}
                placeholder="Ask the lab assistant…"
                className="flex-1 bg-transparent outline-none text-[13px] placeholder:text-[#a89c7e]"
                style={{ color: '#1d160a' }}
                disabled={isSending}
              />
              <button
                onClick={onSend}
                disabled={isSending || !inputValue.trim()}
                className="grid place-items-center w-8 h-8 rounded-lg transition-opacity"
                style={{
                  background: '#7a5a1e',
                  color: '#ffffff',
                  opacity: !inputValue.trim() || isSending ? 0.5 : 1
                }}
              >
                {isSending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const MicVisualizer: React.FC = () => {
  const barsRef = useRef<(HTMLSpanElement | null)[]>([])
  const rafRef = useRef<number | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const NUM_BARS = 6
  // Spread frequency bins across the spectrum to capture bass → treble
  const BIN_INDICES = [2, 5, 10, 18, 28, 42]

  useEffect(() => {
    let ctx: AudioContext | null = null

    navigator.mediaDevices.getUserMedia({ audio: true, video: false }).then((stream) => {
      streamRef.current = stream
      ctx = new AudioContext()
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 128
      analyser.smoothingTimeConstant = 0.7
      source.connect(analyser)
      analyserRef.current = analyser

      const data = new Uint8Array(analyser.frequencyBinCount)

      const tick = () => {
        analyser.getByteFrequencyData(data)
        barsRef.current.forEach((bar, i) => {
          if (!bar) return
          const raw = data[BIN_INDICES[i]] ?? 0
          // Map 0–255 to 3–18px height
          const h = 3 + (raw / 255) * 15
          bar.style.height = `${h}px`
        })
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
    }).catch(() => {
      // mic denied — fall back to static bars
    })

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      streamRef.current?.getTracks().forEach((t) => t.stop())
      ctx?.close()
    }
  }, [])

  return (
    <span className="inline-flex items-center gap-[2.5px] h-[18px]">
      {Array.from({ length: NUM_BARS }).map((_, i) => (
        <span
          key={i}
          ref={(el) => { barsRef.current[i] = el }}
          style={{
            display: 'inline-block',
            width: 2.5,
            height: 3,
            borderRadius: 2,
            background: '#a8543a',
            transition: 'height 60ms ease',
            alignSelf: 'center',
          }}
        />
      ))}
    </span>
  )
}

const SpeakingVisualizer: React.FC = () => {
  const barsRef = useRef<(HTMLSpanElement | null)[]>([])
  const rafRef = useRef<number | null>(null)

  const NUM_BARS = 6
  const BIN_INDICES = [2, 5, 10, 18, 28, 42]

  useEffect(() => {
    resumeAgentAudioContext()
    const analyser = getAgentAudioAnalyser()
    if (!analyser) return

    const data = new Uint8Array(analyser.frequencyBinCount)
    const tick = () => {
      analyser.getByteFrequencyData(data)
      barsRef.current.forEach((bar, i) => {
        if (!bar) return
        const raw = data[BIN_INDICES[i]] ?? 0
        const h = 3 + (raw / 255) * 15
        bar.style.height = `${h}px`
      })
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return (
    <span className="inline-flex items-center gap-[2.5px] h-[18px]">
      {Array.from({ length: NUM_BARS }).map((_, i) => (
        <span
          key={i}
          ref={(el) => { barsRef.current[i] = el }}
          style={{
            display: 'inline-block',
            width: 2.5,
            height: 3,
            borderRadius: 2,
            background: '#7a5a1e',
            transition: 'height 60ms ease',
            alignSelf: 'center',
          }}
        />
      ))}
    </span>
  )
}

const ArtifactInlineCopy: React.FC<{ value: string; kind: string }> = ({ value, kind }) => {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard unavailable - fail silently */
    }
  }
  return (
    <div
      className="flex items-center gap-2 rounded-lg px-2.5 py-1.5"
      style={{ background: '#faf4e3', border: '1px solid #e4d6b0' }}
    >
      <span className="text-[10px] font-semibold tracking-wider uppercase shrink-0" style={{ color: '#b8923a' }}>
        {kind === 'path' ? 'PATH' : 'VALUE'}
      </span>
      <code className="flex-1 min-w-0 text-xs font-mono truncate" style={{ color: '#1d160a' }} title={value}>
        {value}
      </code>
      <button
        onClick={handleCopy}
        className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] transition-colors"
        style={{ color: '#7a6e54' }}
      >
        {copied ? <><Check className="w-3 h-3" /><span>Copied</span></> : <><Copy className="w-3 h-3" /><span>Copy</span></>}
      </button>
    </div>
  )
}

const ChatBubble: React.FC<{ msg: Message }> = ({ msg }) => {
  const isUser = msg.sender === 'user'

  if (msg.artifact) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[90%] min-w-0">
          {msg.artifact.note && (
            <p className="text-[11px] mb-1 italic" style={{ color: '#7a6e54' }}>
              {msg.artifact.note}
            </p>
          )}
          {msg.artifact.kind === 'value' || msg.artifact.kind === 'path' ? (
            <ArtifactInlineCopy value={msg.artifact.content} kind={msg.artifact.kind} />
          ) : (
            <CodeBlock code={msg.artifact.content} language={msg.artifact.language ?? 'text'} />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className="max-w-[85%] px-3 py-2 rounded-xl text-[13px] leading-relaxed whitespace-pre-wrap break-words"
        style={
          isUser
            ? { background: '#7a5a1e', color: '#ffffff', borderTopRightRadius: 4 }
            : {
                background: '#faf4e3',
                color: '#1d160a',
                border: '1px solid #f0e6cd',
                borderTopLeftRadius: 4
              }
        }
      >
        {msg.content}
      </div>
    </div>
  )
}